import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useRole } from '@/stores/identity';
import {
  getMySubscription, daysUntil, statusLabel, tierLabel,
  TRIAL_DAYS, PILOT_DAYS, SALES_EMAIL, ANNUAL_SAVINGS_PERCENT,
} from '@/lib/billing';
import { getCatalog, type SubscriptionPlan } from '@/lib/catalog';
import type { OrgSubscription } from '@/lib/types';
import { PricingComparison } from '@/components/PricingComparison';

export default function Billing() {
  const role = useRole();
  const [sub, setSub] = useState<OrgSubscription | null | 'loading' | 'error'>('loading');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getMySubscription(), getCatalog().catch(() => [])])
      .then(([s, p]) => { if (!cancelled) { setSub(s); setPlans(p); } })
      .catch(() => { if (!cancelled) setSub('error'); });
    return () => { cancelled = true; };
  }, []);

  if (role !== 'owner') {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-3xl mx-auto rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-semibold mb-1">Billing is owner-only</h2>
          <p className="text-sm text-muted-foreground">Ask the owner of your organization to manage the plan.</p>
        </div>
      </div>
    );
  }

  if (sub === 'loading') return <div className="container mx-auto py-8 px-4 text-sm text-muted-foreground">Loading billing…</div>;
  if (sub === 'error' || sub === null) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-3xl mx-auto rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-semibold mb-1">No subscription on file</h2>
          <p className="text-sm text-muted-foreground">
            Contact <a className="underline" href={`mailto:${SALES_EMAIL}`}>{SALES_EMAIL}</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">Billing</h2>
          <p className="text-sm text-muted-foreground">Manage your PharmaSync plan and invoices.</p>
        </div>

        <CurrentPlanCard sub={sub} />
        <ChangePlanSection sub={sub} plans={plans} />
        <InvoicesCard />

        <p className="text-xs text-muted-foreground">
          Need help with billing? Email <a className="underline" href={`mailto:${SALES_EMAIL}`}>{SALES_EMAIL}</a>.
        </p>
      </div>
    </div>
  );
}

function CurrentPlanCard({ sub }: { sub: OrgSubscription }) {
  const heading =
    sub.status === 'trial'    ? 'Trial' :
    sub.status === 'pilot'    ? 'Pilot — design-partner program' :
    sub.status === 'active'   ? `${tierLabel(sub.tier)} plan` :
    sub.status === 'past_due' ? 'Payment past due' : 'Canceled';

  const trialDaysLeft = daysUntil(sub.trial_ends_at);
  const pilotDaysLeft = daysUntil(sub.pilot_ends_at);

  return (
    <section className="rounded-lg border border-border bg-card p-6 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Current plan</h3>
          <p className="text-xl font-semibold mt-1" style={{ color: 'hsl(var(--brand-navy))' }}>{heading}</p>
        </div>
        <StatusPill status={sub.status} />
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm pt-2">
        <Stat label="Status" value={statusLabel(sub.status)} />
        <Stat label="Tier" value={tierLabel(sub.tier)} />
        <Stat label="Billing"
          value={sub.billing_period ? sub.billing_period[0].toUpperCase() + sub.billing_period.slice(1) : '—'} />
        {sub.status === 'trial' && (
          <Stat label="Trial ends"
            value={trialDaysLeft !== null ? `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left` : '—'} />
        )}
        {sub.status === 'pilot' && (
          <Stat label="Pilot ends"
            value={pilotDaysLeft !== null ? `${pilotDaysLeft} day${pilotDaysLeft === 1 ? '' : 's'} left` : '—'} />
        )}
        {sub.status === 'active' && sub.current_period_end && (
          <Stat label="Renews" value={new Date(sub.current_period_end).toLocaleDateString()} />
        )}
      </dl>

      {/* Discount banner if a discount is currently applied to this org */}
      {(sub.discount_pct != null || sub.discount_amount_off_cad != null) && (
        <div className="rounded-md border p-3 text-sm"
          style={{
            borderColor: 'hsl(var(--brand-teal))',
            backgroundColor: 'hsl(var(--brand-teal) / 0.06)',
            color: 'hsl(var(--brand-navy))',
          }}>
          <strong>
            {sub.discount_pct != null
              ? `${sub.discount_pct}% off`
              : `$${sub.discount_amount_off_cad} CAD off`}
          </strong>{' '}
          {sub.discount_ends_at
            ? `until ${new Date(sub.discount_ends_at).toLocaleDateString()}`
            : 'applied to your subscription'}
          {sub.discount_reason && <span className="text-muted-foreground"> · {sub.discount_reason}</span>}
        </div>
      )}

      {sub.status === 'trial' && (
        <p className="text-xs text-muted-foreground">
          We'll email you before your trial ends so you can pick a plan
          without surprises. No credit card on file.
        </p>
      )}
      {sub.status === 'pilot' && (
        <p className="text-xs text-muted-foreground">
          You're in the founding cohort — thank you. Pilot access is free for {PILOT_DAYS} days.
        </p>
      )}
      {sub.internal_note && (
        <p className="text-xs text-muted-foreground italic">Note: {sub.internal_note}</p>
      )}
    </section>
  );
}

function ChangePlanSection({ sub, plans }: { sub: OrgSubscription; plans: SubscriptionPlan[] }) {
  const [period, setPeriod] = useState<'monthly' | 'annual'>(sub.billing_period ?? 'monthly');

  return (
    <section className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Change plan</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Compare side by side. {ANNUAL_SAVINGS_PERCENT}% off when billed annually.
          </p>
        </div>
        <BillingPeriodToggle value={period} onChange={setPeriod} />
      </div>

      <PricingComparison plans={plans} period={period} variant="billing" currentTier={sub.tier ?? null} />

      <p className="text-xs text-muted-foreground">
        Self-serve plan changes will be enabled once payments are connected. Until then,
        email <a className="underline" href={`mailto:${SALES_EMAIL}`}>{SALES_EMAIL}</a> to switch tiers.
        Read more on the <Link to="/pricing" className="underline">pricing page</Link>.
      </p>
      <p className="text-xs text-muted-foreground">
        New customers get a {TRIAL_DAYS}-day free trial. Active subscribers can change tiers anytime —
        change takes effect at the next billing cycle.
      </p>
    </section>
  );
}

function BillingPeriodToggle({
  value, onChange,
}: { value: 'monthly' | 'annual'; onChange: (v: 'monthly' | 'annual') => void }) {
  return (
    <div role="tablist" aria-label="Billing period"
      className="inline-flex items-center rounded-full border border-border p-1 bg-muted/40 self-end">
      <button role="tab" aria-selected={value === 'monthly'} onClick={() => onChange('monthly')}
        className={`rounded-full px-3 py-1 text-xs font-medium ${
          value === 'monthly' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
        }`}>
        Monthly
      </button>
      <button role="tab" aria-selected={value === 'annual'} onClick={() => onChange('annual')}
        className={`rounded-full px-3 py-1 text-xs font-medium ${
          value === 'annual' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
        }`}>
        Annual
        <span className="ml-1.5 inline-block rounded-full px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
          style={{ backgroundColor: 'hsl(var(--brand-teal) / 0.18)', color: 'hsl(var(--brand-navy))' }}>
          -{ANNUAL_SAVINGS_PERCENT}%
        </span>
      </button>
    </div>
  );
}

function InvoicesCard() {
  return (
    <section className="rounded-lg border border-border bg-card p-6 space-y-3">
      <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Invoices</h3>
      <p className="text-sm text-muted-foreground">
        Past invoices will appear here once your subscription becomes active.
      </p>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium" style={{ color: 'hsl(var(--brand-navy))' }}>{value}</dd>
    </div>
  );
}

function StatusPill({ status }: { status: OrgSubscription['status'] }) {
  const tones: Record<OrgSubscription['status'], string> = {
    trial: 'bg-blue-50 text-blue-900 border-blue-200',
    pilot: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    active: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    past_due: 'bg-red-50 text-red-900 border-red-200',
    canceled: 'bg-muted/40 text-muted-foreground border-border',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tones[status]}`}>
      {statusLabel(status)}
    </span>
  );
}
